import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { Button } from '@/components/ui/Button'
import { useTwoFactorConfirm, useTwoFactorSetupQuery } from '@/features/auth/hooks/useAuth'
import { otpCodeSchema } from '@/features/auth/schemas'
import { AuthLayout } from '@/layouts/AuthLayout'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'

const schema = z.object({ code: otpCodeSchema })
type FormValues = z.infer<typeof schema>

export function TwoFactorSetupPage() {
  const navigate = useNavigate()
  const setup = useTwoFactorSetupQuery()
  const confirm = useTwoFactorConfirm()
  const [apiError, setApiError] = useState<unknown>(null)
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { code: '' },
  })

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    confirm.mutate(values, {
      onSuccess: (result) => setRecoveryCodes(result.recoveryCodes),
      onError: (error) => setApiError(error),
    })
  })

  if (recoveryCodes) {
    return (
      <AuthLayout title="Save Your Recovery Codes" subtitle="These are shown only once">
        <div className="alert alert-warning" role="alert">
          Store these somewhere safe. Each code can be used once if you lose access to your
          authenticator app.
        </div>
        <div className="bg-light rounded p-3 mb-3 font-monospace">
          {recoveryCodes.map((code) => (
            <div key={code}>{code}</div>
          ))}
        </div>
        <Button className="w-100" onClick={() => void navigate('/', { replace: true })}>
          I've Saved My Codes
        </Button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Set Up Two-Factor Authentication" subtitle="Required for your account role">
      <FormErrorSummary
        errors={errors}
        serverErrors={getFieldErrors(apiError)}
        generalMessage={apiError ? getErrorMessage(apiError) : null}
      />
      {setup.isPending && <p className="text-muted text-center">Generating your setup code…</p>}
      {setup.data && (
        <>
          <div
            className="d-flex justify-content-center mb-3"
            // Trusted backend-generated SVG (pragmarx/google2fa), not user input.
            dangerouslySetInnerHTML={{ __html: setup.data.qrCodeSvg }}
          />
          <p className="text-muted text-center fs-12 mb-3">
            Scan with Google Authenticator, Authy, or a similar app. Can't scan? Enter this key
            manually: <span className="font-monospace">{setup.data.secret}</span>
          </p>
          <form onSubmit={(e) => void onSubmit(e)} noValidate>
            <FormInput control={control} name="code" label="Enter the 6-digit code" required />
            <Button type="submit" className="w-100" loading={confirm.isPending}>
              Confirm
            </Button>
          </form>
        </>
      )}
    </AuthLayout>
  )
}
